/**
 * User state management with Zustand
 * Handles user authentication and credit management
 * Context: Global user state for LostMind AI Video Lab
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { api } from '../lib/api';
import type { User } from '../lib/types';

// Store interface for user management  
interface UserStore {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  loadUser: () => Promise<void>;
  updateCredits: (amount: number) => Promise<void>;
  purchaseCredits: (amount: number) => Promise<void>;
}

export const useUserStore = create<UserStore>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        isLoading: false,
        error: null,

        loadUser: async () => {
          set({ isLoading: true, error: null });
          
          try {
            const response = await api.getCurrentUser();
            
            if (response.success && response.data) {
              set({ user: response.data, isLoading: false });
            } else {
              // User not authenticated, clear state
              set({ user: null, isLoading: false });
            }
          } catch (error) {
            set({ 
              error: error instanceof Error ? error.message : 'Unknown error',
              user: null,
              isLoading: false 
            });
          }
        },

        updateCredits: async (amount: number) => {
          const currentUser = get().user;
          if (!currentUser) {
            throw new Error('User not authenticated');
          }
          
          // Optimistically update UI
          set({ 
            user: { 
              ...currentUser, 
              credits: currentUser.credits + amount 
            } 
          });
          
          try {
            const response = await api.updateUserCredits(amount);
            
            if (response.success && response.data) {
              set({ user: response.data, error: null });
            } else {
              // Revert optimistic update
              set({ 
                user: currentUser,
                error: response.error || 'Failed to update credits'
              });
              throw new Error(response.error || 'Failed to update credits');
            }
          } catch (error) {
            // Revert optimistic update
            set({ 
              user: currentUser,
              error: error instanceof Error ? error.message : 'Unknown error'
            });
            throw error;
          }
        },

        purchaseCredits: async (amount: number) => {
          set({ isLoading: true, error: null });
          
          try {
            // This would typically create a Stripe session
            // and redirect to checkout, then update credits via webhook
            const response = await api.createPaymentSession('credit-pack', amount);
            
            if (response.success && response.data) {
              // Redirect to Stripe checkout would happen here
              // For now, we'll just update the credits directly
              const currentUser = get().user;
              if (currentUser) {
                set({ 
                  user: { 
                    ...currentUser, 
                    credits: currentUser.credits + amount 
                  },
                  isLoading: false 
                });
              }
            } else {
              throw new Error(response.error || 'Failed to create payment session');
            }
          } catch (error) {
            set({ 
              error: error instanceof Error ? error.message : 'Unknown error',
              isLoading: false 
            });
            throw error;
          }
        },
      }),
      {
        name: 'user-store',
        partialize: (state) => ({ 
          user: state.user,
          // Don't persist loading states or errors
        }),
      }
    ),
    {
      name: 'user-store',
    }
  )
);